import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { isIOSSafari, isStandalone } from '@/utils/platform';

const STORAGE_KEY = 'pwa_install_dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallPrompt: React.FC = () => {
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    if (isIOSSafari()) {
      setIsIOS(true);
      setShow(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt.current) {
      await deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem(STORAGE_KEY, 'true');
      }
      deferredPrompt.current = null;
    }
    setShow(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-[calc(64px+env(safe-area-inset-bottom,0px))] left-0 right-0 z-40 px-4 pb-2">
      <div className="bg-neutral-900 border border-border/60 rounded-lg px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          {isIOS ? (
            <p className="text-sm text-foreground">
              Tap the{' '}
              <svg
                aria-label="Share"
                className="inline h-4 w-4 align-text-bottom"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>{' '}
              button, then <strong>"Add to Home Screen"</strong>
            </p>
          ) : (
            <p className="text-sm text-foreground">
              Add raceqube to your home screen
            </p>
          )}
        </div>
        {!isIOS && (
          <button
            onClick={handleInstall}
            className="text-sm font-medium text-primary flex-shrink-0"
          >
            Install
          </button>
        )}
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
